import { useEffect, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useGetPropertyTypes } from "../../../../api/v1";
import { SelectOption } from "../../../../types/select";
import { convertToTitleCase, mapValueToSelectOption } from "../../../libs/utils";

function equalsIgnoreCase(left?: string, right?: string): boolean {
  return (left ?? "").trim().toUpperCase() === (right ?? "").trim().toUpperCase();
}

function formatUnitSubType(subType: string): string {
  const tokens = subType.split(/(\s+|\+|-|\(|\)|\/)/g);

  const formattedTokens = tokens.map((token) => {
    if (/\d/.test(token)) return token;
    if (/^\s*$|^[-()+/]$/.test(token)) return token;
    return convertToTitleCase(token);
  });

  return formattedTokens.join("");
}

function unitSubTypeToSelectOption(rawSubType: string): SelectOption {
  const formatted = formatUnitSubType(rawSubType);
  return { label: formatted, value: formatted };
}

type PrefillTrackingState = {
  hasMountedUsageTypeWatcher: boolean;
  hasMountedUnitTypeWatcher: boolean;
  hasAppliedPrefillForCurrentUnit: boolean;
  lastPrefilledUnitId: string | undefined;
};

export type UseDependentUnitTypeOptionsParams = {
  shouldPrefillFromUnitData?: boolean;
};

export const useDependentUnitTypeOptions = (params: UseDependentUnitTypeOptionsParams = {}) => {
  const { shouldPrefillFromUnitData = true } = params;
  const prefillTrackingRef = useRef<PrefillTrackingState>({
    hasMountedUsageTypeWatcher: false,
    hasMountedUnitTypeWatcher: false,
    hasAppliedPrefillForCurrentUnit: false,
    lastPrefilledUnitId: undefined,
  });

  const previousUsageTypeValueRef = useRef<string | undefined>(undefined);
  const previousUnitTypeValueRef = useRef<string | undefined>(undefined);

  const { control, setValue } = useFormContext();
  const unitData = useSelector((state: any) => state.propertyDetails.unitData);

  const watchedUsageType: SelectOption = useWatch({ control, name: "usagesType" });
  const watchedUnitType: SelectOption = useWatch({ control, name: "unitType" });
  const hasUsageTypeSelected = Boolean(watchedUsageType?.value);
  const hasUnitTypeSelected = Boolean(watchedUnitType?.value);
  const { propertyTypes, isLoadingPropertyTypes } = useGetPropertyTypes();

  const usageTypeOptions = useMemo(
    () => propertyTypes.map((item) => ({ label: convertToTitleCase(item.usagesType), value: item.usagesType })),
    [propertyTypes],
  );

  const selectedUsageTypeConfig = useMemo(
    () => propertyTypes.find((item) => item.usagesType === watchedUsageType?.value),
    [propertyTypes, watchedUsageType?.value],
  );

  const unitTypeOptions = useMemo(() => {
    if (!selectedUsageTypeConfig) return [];

    return Object.keys(selectedUsageTypeConfig.unitType).map((type) => ({
      label: convertToTitleCase(type),
      value: type,
    }));
  }, [selectedUsageTypeConfig]);

  const unitSubTypeOptions = useMemo(() => {
    if (!selectedUsageTypeConfig) return [];

    const subTypes = selectedUsageTypeConfig.unitType[watchedUnitType?.value];
    if (!subTypes) return [];

    return subTypes.map((subType) => ({ label: formatUnitSubType(subType), value: formatUnitSubType(subType) }));
  }, [selectedUsageTypeConfig, watchedUnitType?.value]);

  // Prefill usage / unit type / sub type from Redux unit payload once property types are loaded.
  useEffect(() => {
    if (!shouldPrefillFromUnitData) return;

    const currentUnitId = unitData?.unitId != null ? String(unitData.unitId) : "";
    const tracking = prefillTrackingRef.current;

    if (tracking.lastPrefilledUnitId !== currentUnitId) {
      tracking.lastPrefilledUnitId = currentUnitId;
      tracking.hasAppliedPrefillForCurrentUnit = false;
      previousUsageTypeValueRef.current = undefined;
      previousUnitTypeValueRef.current = undefined;
    }

    if (tracking.hasAppliedPrefillForCurrentUnit || !unitData?.unit || !propertyTypes.length) return;

    const { usagesType: savedUsagesType, unitType: savedUnitType, unitSubType: savedUnitSubType } = unitData.unit;

    const usageRowFromCatalog = savedUsagesType
      ? propertyTypes.find((row) => equalsIgnoreCase(row.usagesType, savedUsagesType))
      : undefined;

    if (usageRowFromCatalog) {
      setValue("usagesType", mapValueToSelectOption(usageRowFromCatalog.usagesType, { isTitleCaseLabel: true }));
    }

    const catalogUnitTypeKey =
      usageRowFromCatalog && savedUnitType
        ? Object.keys(usageRowFromCatalog.unitType).find((key) => equalsIgnoreCase(key, savedUnitType))
        : undefined;

    if (catalogUnitTypeKey && usageRowFromCatalog) {
      setValue("unitType", mapValueToSelectOption(catalogUnitTypeKey, { isTitleCaseLabel: true }));

      const catalogSubTypes = usageRowFromCatalog.unitType[catalogUnitTypeKey] ?? [];
      const catalogSubTypeMatch = savedUnitSubType
        ? catalogSubTypes.find((candidate) => equalsIgnoreCase(candidate, savedUnitSubType))
        : undefined;

      setValue("unitSubType", catalogSubTypeMatch ? unitSubTypeToSelectOption(catalogSubTypeMatch) : null);
    } else if (
      (usageRowFromCatalog && !savedUnitType) ||
      (Boolean(savedUnitType) && catalogUnitTypeKey === undefined)
    ) {
      setValue("unitType", null);
      setValue("unitSubType", null);
    }

    tracking.hasAppliedPrefillForCurrentUnit = true;
  }, [setValue, shouldPrefillFromUnitData, unitData, propertyTypes]);

  // Reset unit type and sub type when the user changes usage type (skip the initial empty → value transition).
  useEffect(() => {
    const currentUsageValue = watchedUsageType?.value as string | undefined;
    const tracking = prefillTrackingRef.current;

    if (!tracking.hasMountedUsageTypeWatcher) {
      tracking.hasMountedUsageTypeWatcher = true;
      previousUsageTypeValueRef.current = currentUsageValue;
      return;
    }

    const previousUsageValue = previousUsageTypeValueRef.current;
    previousUsageTypeValueRef.current = currentUsageValue;

    const usageTypeChangedFromRealValue = Boolean(previousUsageValue) && previousUsageValue !== currentUsageValue;
    if (!usageTypeChangedFromRealValue) return;

    setValue("unitType", null);
    setValue("unitSubType", null);
  }, [setValue, watchedUsageType?.value]);

  // Reset sub type when the user changes unit type (skip the initial empty → value transition).
  useEffect(() => {
    const currentUnitTypeValue = watchedUnitType?.value as string | undefined;
    const tracking = prefillTrackingRef.current;

    if (!tracking.hasMountedUnitTypeWatcher) {
      tracking.hasMountedUnitTypeWatcher = true;
      previousUnitTypeValueRef.current = currentUnitTypeValue;
      return;
    }

    const previousUnitTypeValue = previousUnitTypeValueRef.current;
    previousUnitTypeValueRef.current = currentUnitTypeValue;

    const unitTypeChangedFromRealValue =
      Boolean(previousUnitTypeValue) && previousUnitTypeValue !== currentUnitTypeValue;
    if (!unitTypeChangedFromRealValue) return;

    setValue("unitSubType", null);
  }, [setValue, watchedUsageType?.value, watchedUnitType?.value]);

  return {
    propertyTypes,
    isLoadingPropertyTypes,
    hasUsageTypeSelected,
    hasUnitTypeSelected,
    usageTypeOptions,
    unitTypeOptions,
    unitSubTypeOptions,
  };
};
